<script>
    import { createEventDispatcher } from "svelte";
    import { writable, get } from "svelte/store";
    import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
    import { faXmark, faTrashCan, faSquarePlus } from '@fortawesome/pro-light-svg-icons';

    const dispatch = createEventDispatcher();
    export let showForm = false;
    export let editRole = null;

    // Permission types and resources for the form
    const resourceTypes = ['route', 'menu', 'field', 'action'];
    const accessTypes = ['read', 'write', 'create', 'delete', 'none'];

    let roleData = writable({
        name: "",
        displayName: "",
        description: "",
        permissions: [],
        isSystem: false,
        isActive: true
    });

    $: if (editRole) {
        roleData.set(editRole);
    }

    $: if (showForm && !editRole) {
        resetRoleData();
    }

    function resetRoleData() {
        roleData.set({
            name: "",
            displayName: "",
            description: "",
            permissions: [],
            isSystem: false,
            isActive: true
        });
    }

    function close() {
        dispatch("close");
    }

    function handleSubmit() {
        dispatch("submit", get(roleData));
        close();
    }

    // Format permission display
    function formatPermission(permission) {
        return `${permission.resource}:${permission.identifier} (${permission.access})`;
    }
</script>

{#if showForm}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
        <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={close}></div>
        <div 
            class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-3xl relative z-60 overflow-y-auto max-h-[90vh]"
            on:click|stopPropagation
        >
            <header class="flex justify-between items-start mb-4">
                <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {editRole ? "Edit Role" : "New Role"}
                </h2>
                <button
                    on:click={close}
                    class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </header>
            <form on:submit|preventDefault={handleSubmit} class="space-y-4">
                <div class="flex gap-3">
                    <div class="w-5/12">
                        <label
                            for="name"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Name *</label
                        >
                        <input
                            type="text"
                            id="name"
                            bind:value={$roleData.name}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            required
                            disabled={editRole?.isSystem}
                        />
                    </div>
                    <div class="w-full">
                        <label
                            for="displayName"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Display Name *</label
                        >
                        <input
                            type="text"
                            id="displayName"
                            bind:value={$roleData.displayName}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            required
                        />
                    </div>
                    <div class="w-2/12">
                        <label
                            for="active"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Active *</label
                        >
                        <select
                            id="active"
                            bind:value={$roleData.isActive}
                            class="form-select h-8 w-32 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        >
                            <option value={true}>Yes</option>
                            <option value={false}>No</option>
                        </select>
                    </div>
                </div>

                <div class="mb-4">
                    <label
                        for="description"
                        class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                        >Description</label
                    >
                    <textarea
                        id="description"
                        bind:value={$roleData.description}
                        rows="3"
                        class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    ></textarea>
                </div>

                <div class="mb-4">
                    <div class="flex justify-between items-center mb-2">
                        <label class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Permissions</label>
                        <button
                            type="button"
                            on:click={() => $roleData.permissions = [...$roleData.permissions, { resource: 'route', identifier: '', access: 'read' }]}
                            class="pagination mx-1 p-0"
                            title="Add permission"
                        >
                            <FontAwesomeIcon icon={faSquarePlus} class="text-sky-800 dark:text-white/70" />
                        </button>
                    </div>
                    <div class="space-y-2">
                        {#each $roleData.permissions as permission, index}
                            <div class="flex items-center space-x-2">
                                <select
                                    bind:value={permission.resource}
                                    class="border p-1 w-24 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                >
                                    {#each resourceTypes as type}
                                        <option value={type}>{type}</option>
                                    {/each}
                                </select>
                                <input
                                    type="text"
                                    bind:value={permission.identifier}
                                    placeholder="Identifier"
                                    class="flex-1 border p-1 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                />
                                <select
                                    bind:value={permission.access}
                                    class="border p-1 w-24 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                >
                                    {#each accessTypes as type}
                                        <option value={type}>{type}</option>
                                    {/each}
                                </select>
                                <button
                                    type="button"
                                    on:click={() => $roleData.permissions = $roleData.permissions.filter((_, i) => i !== index)}
                                    class="pagination mx-1 p-0"
                                    title="Remove permission"
                                >
                                    <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                                </button>
                            </div>
                        {/each}
                    </div>
                </div>

                <div class="flex justify-end space-x-2 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                        type="button"
                        class="bg-gray-500 text-white py-1 px-2 rounded hover:bg-gray-700"
                        on:click={close}
                        >Cancel</button
                    >
                    <button
                        type="submit"
                        class="bg-blue-500 text-white py-1 px-2 rounded hover:bg-blue-700"
                        >Save</button
                    >
                </div>
            </form>
        </div>
    </div>
{/if} 